import React, { useState, useRef, useEffect } from 'react';
import { Shield, Loader2 } from 'lucide-react';

const OtpVerifyStep = ({ onVerify, onCancel, loading }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [trustDevice, setTrustDevice] = useState(false);
  const [trustDays, setTrustDays] = useState(7);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputs.current[index + 1]?.focus();
    }
    // Auto-submit when all 6 digits entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        onVerify(fullCode, trustDevice, trustDays);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      inputs.current[5]?.focus();
      onVerify(pasted, trustDevice, trustDays);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length === 6) {
      onVerify(fullCode, trustDevice, trustDays);
    }
  };

  return (
    <div className="space-y-6" data-testid="otp-verify-step">
      <div className="text-center">
        <div className="w-14 h-14 bg-[#E8F0FE] rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-7 h-7 text-[#1A73E8]" />
        </div>
        <h2 className="text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
          Two-Factor Authentication
        </h2>
        <p className="text-[#5F6368] text-sm mt-1">
          Enter the 6-digit code from your authenticator app
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={el => inputs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              className="w-12 h-14 text-center text-xl font-mono border-2 border-[#DADCE0] rounded-lg focus:border-[#1A73E8] focus:ring-1 focus:ring-[#1A73E8] outline-none transition-colors"
              data-testid={`otp-input-${i}`}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 mb-4 justify-center">
          <input
            type="checkbox" id="trust-device"
            checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)}
            className="w-4 h-4 text-[#1A73E8] rounded"
            data-testid="trust-device-checkbox"
          />
          <label htmlFor="trust-device" className="text-sm text-[#5F6368]">
            Trust this device for
          </label>
          <select
            value={trustDays} onChange={e => setTrustDays(parseInt(e.target.value))}
            className="text-sm border rounded px-2 py-0.5"
            data-testid="trust-days-select"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={loading || code.join('').length !== 6}
          className="btn-google w-full flex items-center justify-center gap-2"
          data-testid="verify-otp-btn"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</> : 'Verify & Sign In'}
        </button>
      </form>

      <div className="text-center">
        <button onClick={onCancel} className="text-sm text-[#5F6368] hover:text-[#202124]" data-testid="otp-cancel-btn">
          Back to login
        </button>
        <p className="text-xs text-[#9AA0A6] mt-3">
          Lost your device? Use a recovery code or contact admin.
        </p>
      </div>
    </div>
  );
};

export default OtpVerifyStep;
